import {
  Component,
  Input,
  OnInit,
  ChangeDetectorRef,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { CustomerService } from 'src/app/shareModule/services/customer.service';
import { selectUser } from 'src/app/shareModule/states/user-details/user-details.selector';
import { select } from '@ngrx/store';
import { User } from 'src/app/shareModule/models/user.model';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/shareModule/states/app.state';
import { RoleService } from 'src/app/shareModule/services/roles/role.service';
import { ToastrService } from 'ngx-toastr';

interface Item {
  id: string;
  name: string;
  price: number;
  priceMax?: number;
  unit: string;
  type: 'number' | 'range';
  filter?: string;
}

interface SelectedItem {
  item: Item;
  quantity: number;
  minRange?: number;
  maxRange?: number;
  total: number;
}

@Component({
  selector: 'app-map-calculator',
  templateUrl: './map-calculator.component.html',
  styleUrls: ['./map-calculator.component.scss'],
})
export class MapCalculatorComponent implements OnInit, OnChanges {
  @Input() filter: string = '';

  // Items management
  items: Item[] = [];
  selectedItems: SelectedItem[] = [];
  showAddItemModal = false;
  modalRef: NgbModalRef | null = null;

  // New item form
  newItem: Partial<Item> = {
    name: '',
    price: 0,
    priceMax: 0,
    unit: '',
    type: 'number',
  };

  // Overall total
  overallTotal: number = 0;

  constructor(
    private modalService: NgbModal,
    private customerService: CustomerService,
    private store: Store<AppState>,
    private roleService: RoleService,
    private cdr: ChangeDetectorRef,
    private toastr: ToastrService,
  ) {}

  user: User;
  isAdmin = false;

  ngOnInit(): void {
    this.store.pipe(select(selectUser)).subscribe((user: User) => {
      this.user = user;
      this.roleService.rolesSubject.subscribe((response) => {
        this.isAdmin = response?.userType?.includes('ADMIN');
        this.cdr.detectChanges();
      });
      this.cdr.detectChanges();
    });
    this.getMapCalculator();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter']) {
      this.getMapCalculator();
    }
  }

  getMapCalculator(): void {
    this.customerService.getMapCalculator({ filter: this.filter }).subscribe(
      (res: any) => {
        if (res && res.body) {
          this.items = res.body;
        }
      },
      (error: any) => {
        console.error('Error fetching map calculator items:', error);
      },
    );
  }

  // Open add item modal
  openAddItemModal(content: any): void {
    // Reset the form data
    this.resetNewItem();
    this.modalRef = this.modalService.open(content, {
      size: 'md',
      centered: true,
      // backdrop: 'static'
    });

    // Handle modal dismissal
    this.modalRef.result.then(
      (result) => {
        // Modal was closed successfully
        this.modalRef = null;
      },
      (reason) => {
        // Modal was dismissed
        this.modalRef = null;
        this.resetNewItem();
      },
    );
  }

  // Reset new item form
  resetNewItem(): void {
    this.newItem = {
      name: '',
      price: 0,
      priceMax: 0,
      unit: '',
      type: 'number',
    };
    this.cdr.detectChanges();
  }

  // Handle type change
  onTypeChange(): void {
    if (this.newItem.type === 'number') {
      this.newItem.priceMax = 0;
    }
    this.cdr.detectChanges();
  }

  // Handle input changes to ensure proper binding
  onInputChange(field: string, value: any): void {
    (this.newItem as any)[field] = value;
    this.cdr.detectChanges();
  }

  // Close modal
  closeModal(): void {
    if (this.modalRef) {
      this.modalRef.close();
      this.modalRef = null;
    }
    this.resetNewItem();
  }

  // Add new item
  addItem(): void {
    // Validate form data
    if (
      !this.newItem.name ||
      !this.newItem.unit ||
      this.newItem.price === null ||
      this.newItem.price === undefined
    ) {
      console.error('Please fill in all required fields');
      return;
    }

    const payload = {
      name: this.newItem.name,
      price: this.newItem.price,
      priceMax: this.newItem.priceMax,
      unit: this.newItem.unit,
      type: this.newItem.type,
      filter: this.filter,
    };

    this.customerService.addMapCalculator(payload).subscribe(
      (res: any) => {
        // Reset form
        this.resetNewItem();

        // Refresh the items list
        this.getMapCalculator();
        this.closeModal();

        // Show success message (optional)
        this.toastr.success('Item added successfully');
      },
      (error: any) => {
        console.error('Error adding map calculator item:', error);
      },
    );
  }

  // Remove item
  removeItem(itemId: string): void {
    this.customerService.deleteMapCalculator(Number(itemId)).subscribe(
      (res: any) => {
        // Remove from local arrays
        this.items = this.items.filter((item) => item.id !== itemId);
        this.selectedItems = this.selectedItems.filter(
          (selected) => selected.item.id !== itemId,
        );
        this.calculateOverallTotal();

        // Refresh the items list to ensure consistency
        this.getMapCalculator();
        this.closeModal();

        this.toastr.success('Item deleted successfully');
      },
      (error: any) => {
        console.error('Error deleting map calculator item:', error);
        this.toastr.error('Failed to delete item');
      },
    );
  }

  // Select item for calculation (only one item at a time)
  selectItem(item: Item): void {
    // Clear previous selection and select new item
    this.selectedItems = [
      {
        item: item,
        quantity: 1,
        total: item.price,
      },
    ];
    this.calculateOverallTotal();
  }

  // Remove selected item
  removeSelectedItem(itemId: string): void {
    this.selectedItems = this.selectedItems.filter(
      (selected) => selected.item.id !== itemId,
    );
    this.calculateOverallTotal();
  }

  // Update quantity
  updateQuantity(itemId: string, quantity: number): void {
    const selectedItem = this.selectedItems.find(
      (selected) => selected.item.id === itemId,
    );
    if (selectedItem) {
      selectedItem.quantity = quantity;
      selectedItem.total = selectedItem.item.price * quantity;
      this.calculateOverallTotal();
    }
  }

  // Update quantity for range items (user only enters quantity)
  updateRangeQuantity(itemId: string, quantity: number): void {
    const selectedItem = this.selectedItems.find(
      (selected) => selected.item.id === itemId,
    );
    if (selectedItem && selectedItem.item.type === 'range') {
      selectedItem.quantity = quantity;
      // For range items, we'll calculate min and max totals based on price and priceMax
      selectedItem.total = selectedItem.item.price * selectedItem.quantity;
      this.calculateOverallTotal();
    }
  }

  // Get range total display for range items
  getRangeTotalDisplay(selectedItem: SelectedItem): string {
    if (selectedItem.item.type === 'range' && selectedItem.item.priceMax) {
      // Use price and priceMax from the item
      const minTotal = selectedItem.item.price * selectedItem.quantity;
      const maxTotal = selectedItem.item.priceMax * selectedItem.quantity;
      return `$${minTotal.toFixed(2)} - $${maxTotal.toFixed(2)}`;
    }
    return `$${selectedItem.total.toFixed(2)}`;
  }

  // Get overall range total display
  getOverallRangeTotal(): string {
    let minTotal = 0;
    let maxTotal = 0;

    this.selectedItems.forEach((selected) => {
      if (selected.item.type === 'range' && selected.item.priceMax) {
        // Use price and priceMax from the item
        const itemMinTotal = selected.item.price * selected.quantity;
        const itemMaxTotal = selected.item.priceMax * selected.quantity;
        minTotal += itemMinTotal;
        maxTotal += itemMaxTotal;
      } else {
        minTotal += selected.total;
        maxTotal += selected.total;
      }
    });

    if (minTotal === maxTotal) {
      return `$${minTotal.toFixed(2)}`;
    }
    return `$${minTotal.toFixed(2)} - $${maxTotal.toFixed(2)}`;
  }

  // Calculate overall total
  calculateOverallTotal(): void {
    this.overallTotal = this.selectedItems.reduce(
      (sum, selected) => sum + selected.total,
      0,
    );
  }

  // Check if item is selected
  isItemSelected(itemId: string): boolean {
    return this.selectedItems.some((selected) => selected.item.id === itemId);
  }
}
